import { Controller } from "@hotwired/stimulus";
import { useClickOutside } from "stimulus-use";

export default class extends Controller {
  static targets = ["query", "results"];
  static values = { url: String, compact: Boolean };

  connect() {
    useClickOutside(this);
    this.model = null;
    this.abortController = new AbortController();
    this.previousLength = 0;
    this.MIN_LENGTH_FOR_SEARCH = 3;
  }

  disconnect() {
    this.reset();
  }

  onFocus(event) {
    this.showSkeleton();
    this.doFetch(event);
  }

  fetchResults(event, forceFetch = false) {
    if (
      forceFetch ||
      this.queryLongEnough() ||
      this.queryLengthPassedBelowResetThreshold()
    ) {
      this.showLoader();
      if (this.timeout) clearTimeout(this.timeout);
      this.timeout = setTimeout(() => this.doFetch(event), 400);
    }
    this.previousLength = this.queryTarget.value.trim().length;
  }

  queryLongEnough() {
    return this.queryTarget.value.trim().length >= this.MIN_LENGTH_FOR_SEARCH;
  }

  queryLengthPassedBelowResetThreshold() {
    return (
      this.queryTarget.value.trim().length < this.MIN_LENGTH_FOR_SEARCH &&
      this.previousLength >= this.MIN_LENGTH_FOR_SEARCH
    );
  }

  showLoader() {
    if (this.queryTarget.value.trim() === "") return;

    const mainContainer = this.resultsTarget.querySelector(
      "#search-result--main",
    );
    const historyContainer = this.resultsTarget.querySelector(
      "#search-result--history",
    );
    const resultsContainer = this.resultsTarget.querySelector(
      "#search-results-container",
    );

    if (mainContainer) mainContainer.classList.remove("hidden");
    if (historyContainer) historyContainer.classList.add("hidden");

    if (
      resultsContainer &&
      !resultsContainer.innerHTML.includes("Recherche...")
    ) {
      resultsContainer.innerHTML = `
        <div class="text-center my-4 text-brand-text-muted">
          <div role="status" class="inline-flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="animate-spin"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
            <span>Recherche...</span>
          </div>
        </div>`;
    }
  }

  doFetch(event) {
    if (event?.key === "Escape") {
      this.reset();
      return;
    }

    if (!this.compactValue) {
      this.queryTarget.classList.add(
        "md:min-w-[300px]",
        "lg:min-w-[470px]",
        "xl:min-w-[628px]",
      );
    }

    const queryValue =
      this.queryTarget.value.trim().length < this.MIN_LENGTH_FOR_SEARCH
        ? ""
        : this.queryTarget.value.trim();

    const url = new URL(this.urlValue, window.location.origin);
    if (this.model) {
      url.searchParams.append("model", this.model);
    }
    url.searchParams.append("query", queryValue);

    this.abortPreviousFetchRequest();
    this.abortController = new AbortController();

    fetch(url, { signal: this.abortController.signal })
      .then((response) => response.text())
      .then((html) => {
        this.resultsTarget.innerHTML = html;
      })
      .catch(() => {});
  }

  selectModel(event) {
    this.model = event.target.dataset.model;
    this.fetchResults(null, true);
  }

  navigateResults(event) {
    // keyboard navigation placeholder — extend as needed
  }

  clickOutside() {
    this.resultsTarget.innerHTML = "";
    this.queryTarget.value = "";
    this.queryTarget.classList.remove(
      "md:min-w-[300px]",
      "lg:min-w-[470px]",
      "xl:min-w-[628px]",
    );
  }

  reset() {
    this.resultsTarget.innerHTML = "";
    this.queryTarget.value = "";
    this.queryTarget.classList.remove(
      "md:min-w-[300px]",
      "lg:min-w-[470px]",
      "xl:min-w-[628px]",
    );
  }

  abortPreviousFetchRequest() {
    if (this.abortController) {
      this.abortController.abort();
    }
  }

  showSkeleton() {
    if (this.resultsTarget.innerHTML.trim() !== "") return;

    const modelList = `
      <ul class="flex flex-col">
        ${Array(3)
          .fill()
          .map(
            () => `
          <li class="flex items-center py-2 px-1.5">
            <div class="size-7 rounded-full bg-gray-200 mr-3 animate-pulse"></div>
            <div class="h-4 bg-gray-200 rounded w-20 animate-pulse"></div>
          </li>
        `,
          )
          .join("")}
      </ul>
    `;
    this.resultsTarget.innerHTML = `
      <div class="absolute grid grid-cols-3 z-50 overflow-y-scroll px-4 py-5 bg-white shadow-xl rounded-4xl mt-2 md:min-w-75 lg:min-w-117.5 xl:min-w-157 max-w-157 max-h-157.5 shadow-brand-purple-dark/30 divide-x-1 divide-brand-purple-border gap-4">
        <div class="col-span-1 pe-3">
          ${modelList}
        </div>
        <div class="col-span-2">
          <div id="search-result--history">
            <h3 class="uppercase border-b border-brand-purple-border font-normal font-bevellier tracking-wider text-brand-text-muted px-1.5 pb-2.5">Vus récemment</h3>
            <div class="animate-pulse">
              <div class="flex items-center py-2 px-1.5"><div class="h-4 bg-gray-200 rounded w-3/4"></div></div>
              <div class="flex items-center py-2 px-1.5"><div class="h-4 bg-gray-200 rounded w-1/2"></div></div>
              <div class="flex items-center py-2 px-1.5"><div class="h-4 bg-gray-200 rounded w-3/4"></div></div>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}
